/**
 * PKCE OAuth implementation for MyID authentication
 */

const MYID_BASE_URL = 'https://sso.myid.disney.com';
const OAUTH_SERVER_ID = 'aus12rjp71ht7GjQl0x8';
const CLIENT_ID = process.env.VUE_APP_CLIENT_ID || 'parklink-latest';
const MYID_LOGOUT_URL = process.env.VUE_APP_MYID_LOGOUT_URL || 'https://sso.myid.disney.com/login/signout';
const AUTH_TOKEN_KEY = 'authToken';
const CODE_VERIFIER_KEY = 'codeVerifier';

interface MyIdConfig {
  authorizationUrl: string;
  tokenEndpoint: string;
  wrapperTokenEndpoint: string;
  validateEndpoint: string;
  logoutEndpoint: string;
  redirectUri: string;
  clientId: string;
  scope: string;
}

function getConfig(): MyIdConfig {
  const baseUrl = (process.env.VUE_APP_BASE_URL || '').replace(/\/$/, '');
  return {
    authorizationUrl: `${MYID_BASE_URL}/oauth2/${OAUTH_SERVER_ID}/v1/authorize`,
    tokenEndpoint: `${MYID_BASE_URL}/oauth2/${OAUTH_SERVER_ID}/v1/token`,
    wrapperTokenEndpoint: `${baseUrl}/api/auth/oidc`,
    validateEndpoint: `${baseUrl}/api/auth/validate`,
    logoutEndpoint: `${baseUrl}/api/auth/logout`,
    redirectUri: `${window.location.origin}/parklink-status`,
    clientId: CLIENT_ID,
    scope: 'openid profile',
  };
}

function generateCodeVerifier(): string {
  const length = Math.floor(Math.random() * (128 - 43 + 1)) + 43;
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let codeVerifier = '';

  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < length; i++) {
    codeVerifier += characters.charAt(randomValues[i] % characters.length);
  }

  return codeVerifier;
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(digest));
  const base64 = btoa(String.fromCharCode(...hashArray));

  return base64.replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function generateNonce(): string {
  const randomValues = new Uint8Array(16);
  crypto.getRandomValues(randomValues);
  return Array.from(randomValues, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(CODE_VERIFIER_KEY);
}

function isOnCallbackPage(): boolean {
  const url = new URL(window.location.href);
  const isCallbackPath = url.pathname === '/parklink-status' || 
                         url.pathname === '/parklink-analytics' ||
                         url.pathname.startsWith('/parklink-analytics/');
  return isCallbackPath && url.searchParams.has('code');
}

// Redirect to MyID for authentication
export async function redirectToLogin(state?: string): Promise<void> {
  const config = getConfig();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const nonce = generateNonce();

  // Store code verifier for later use
  localStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);

  const params = new URLSearchParams({
    response_type: 'code',
    scope: config.scope,
    redirect_uri: config.redirectUri,
    client_id: config.clientId,
    nonce: nonce,
    code_challenge_method: 'S256',
    response_mode: 'query',
    state: state || window.location.href,
    code_challenge: codeChallenge,
  });

  window.location.href = `${config.authorizationUrl}?${params.toString()}`;
}

// Exchange authorization code for tokens
async function exchangeCodeForToken(code: string): Promise<{ access_token: string; id_token?: string }> {
  const config = getConfig();
  const codeVerifier = localStorage.getItem(CODE_VERIFIER_KEY);

  if (!codeVerifier) {
    throw new Error('Code verifier not found');
  }

  const body = new URLSearchParams({
    client_id: config.clientId,
    code: code,
    code_verifier: codeVerifier,
    grant_type: 'authorization_code',
    redirect_uri: config.redirectUri,
    scope: config.scope,
  });

  const response = await fetch(config.tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: body.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
  }

  return response.json();
}

// Get wrapper token from backend
async function getWrapperToken(authToken: { access_token: string; id_token?: string }): Promise<{ jwt: string }> {
  const config = getConfig();

  const response = await fetch(config.wrapperTokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(authToken),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Wrapper token failed: ${response.status} ${errorText}`);
  }

  return response.json();
}

// Validate token with backend (used for session checks)
export async function validateToken(): Promise<boolean> {
  const token = getAuthToken();
  if (!token) {
    return false;
  }

  const config = getConfig();
  try {
    const response = await fetch(config.validateEndpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      const text = await response.text();
      clearAuthToken();
      return false;
    }
    return true;
  } catch (err) {
    console.error('[MyID] validateToken: Fetch error', err);
    clearAuthToken();
    return false;
  }

  return true;
}

export async function initLoginFlow(): Promise<{ authenticated: boolean; redirectUrl?: string }> {
  const existingToken = getAuthToken();
  if (existingToken) {
    const isValid = await validateToken();
    if (isValid) {
      return { authenticated: true };
    }

    clearAuthToken();
  }

  if (isOnCallbackPage()) {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');
    const error = url.searchParams.get('error');

    if (error) {
      const errorDescription = url.searchParams.get('error_description');
      localStorage.removeItem(CODE_VERIFIER_KEY);
      throw new Error(`MyID error: ${error} - ${errorDescription}`);
    }

    if (!code) {
      localStorage.removeItem(CODE_VERIFIER_KEY);
      throw new Error('Authorization code not found in callback');
    }

    try {
      const authToken = await exchangeCodeForToken(code);
      const wrapperToken = await getWrapperToken(authToken);

      setAuthToken(wrapperToken.jwt);

      const redirectUrl = state ? decodeURIComponent(state) : '/parklink-status';
      return { authenticated: true, redirectUrl };
    } finally {
      localStorage.removeItem(CODE_VERIFIER_KEY);
    }
  }

  await redirectToLogin();

  return { authenticated: false };
}

export async function logout(): Promise<void> {
  clearAuthToken();

  const config = getConfig();
  try {
    const response = await fetch(config.logoutEndpoint, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (data.logoutEndpoint) {
      window.location.href = data.logoutEndpoint;
      return;
    }
  } catch (e) {
    console.error('[MyID] Logout error:', e);
  }

  window.location.href = MYID_LOGOUT_URL;
}
